import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as _ from 'lodash';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

export type CustomSelectOptions = Array<{
  label: string;
  subDescription?: string;
  value: any;
  isGroupOption?: boolean;
  parentValue?: any;
  hide?: boolean;
}>;

@Component({
  selector: 'custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CustomSelect),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomSelect implements OnInit, OnChanges, ControlValueAccessor {
  @Input() isGroupOption: boolean = false;
  @Input() placeholder: string = "Select";
  @Input() options: CustomSelectOptions;
  @Input() canSearch: boolean;
  @Input() disabled: any = false;

  @Input() selected: any = '';
  @Output() selectedChange = new EventEmitter();

  public selectedControl: FormControl = new FormControl();

  private subjecValueSearch = new Subject<any>();

  selectedOption: any;
  filteredOptions = [];
  allOptions = [];
  searchInput = '';

  onChange = (value: string) => {};
  onTouched = () => {};

  constructor(
    private readonly cdr: ChangeDetectorRef,

  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.options &&
      changes.options.currentValue !== changes.options.previousValue
    ) {
      this.allOptions = !this.isGroupOption
        ? this.options.map((item) => item.value)
        : this.options
            .filter((item) => !item.isGroupOption)
            .map((item) => item.value);

      this.filteredOptions = [...(this.options || [])];

      if (
        changes.disabled &&
        changes.disabled.currentValue !== changes.disabled.previousValue
      ) {
        if (changes.disabled.currentValue) {
          this.selectedControl.disable();
          this.placeholder = '';
        } else {
          this.selectedControl.enable();
          this.placeholder = 'Select';
        }
      }
    }

    if (changes.selected && changes.selected.currentValue !== this.selectedControl.value) {

      setTimeout(() => { this.selectedControl.setValue(this.selected) }, 0)
    }
  }

  ngOnInit() {
    this.selectedControl.valueChanges.subscribe((value) => {
      this.selectedChange.emit(value);
      this.onChange(value);
      this.onTouched();
      this.onSelect(value);
    });
  }

  writeValue(obj: any): void {
    this.selectedControl.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public onSelectClick() {
    setTimeout(() => {
      const listContainer = document.getElementsByClassName('options-list');
      listContainer.length && listContainer[0].scroll({ top: 0 });
    }, 50); // TODO Temporarily solve the problem of scrolling incorrectly during the first render, please check
    if (this.canSearch) {
      if (this.searchInput) {
        this.searchInput = '';
        // this.subjecValueSearch.next("");
        this.searchOptions('');
      }
    }
  }

  ngAfterViewInit() {
    this.subjecValueSearch
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((value) => {
        this.searchOptions(value);
      });
  }

  private searchOptions(searchString: string) {
    if (searchString) searchString = searchString.trim();
    if (!searchString) this.filteredOptions = [...(this.options || [])];
    else {
      if (this.isGroupOption) {
        this.filteredOptions = this.options.map((item) => {
          if (
            item.label.toLowerCase().includes(searchString.toLowerCase()) ||
            item.isGroupOption
          )
            return item;
          else {
            return { ...item, hide: true };
          }
        });
      } else {
        this.filteredOptions = this.options.map((item) => {
          if (item.label.toLowerCase().includes(searchString.toLowerCase()))
            return item;
          if (item.subDescription && item.subDescription.toLowerCase().includes(searchString.toLowerCase()))
            return item;
          else {
            return { ...item, hide: true };
          }
        });
      }
    }
    this.cdr.markForCheck();
  }

  public handleSearch(searchString: string) {
    setTimeout(() => {
      const listContainer = document.getElementById('search-input');
      listContainer && listContainer.focus();
    }, 200);
    this.subjecValueSearch.next(searchString || null);
  }

  public onSelect(value) {
    this.selectedOption = this.options.find((item) => item.value === value);
  }

  isEmptyData() {
    return this.options.length == 0 || this.filteredOptions.every(item => item.hide)
  }
}
